import { ExpandMore } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
} from '@mui/material';
import { cloneDeep, isEmpty, set } from 'lodash';
import { useState } from 'react';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { VariationSizeNone } from 'src/components/widget/lineupWidget/editProductPanel/EditProductPanel.styled';
import { getPlainText, lang, moveArrayItem } from 'src/utils';

import { ProductVariationFlavor } from './modules/productVariationFlavor/ProductVariationFlavor';
import {
  flavourWithOrderKey,
  renderArrayBasedOnOrderSort,
  updateFlavorImages,
} from './ProductVariationsAccordion.helper';
import { ProductVariationsAccordionProps } from './ProductVariationsAccordion.interface';
import {
  BoldTypography,
  ColouredDragIcon,
  FlexGrid,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionItem,
  StyledCheckBoxWrapper,
  StyledInfoWrapper,
  StyledOptionWrapper,
  StyledShowFlavorCount,
  StyledWrapper,
} from './ProductVariationsAccordion.styled';

export const VariationName = ({ name }: { name?: string }) => {
  return (
    <CustomTooltip
      width={500}
      placement="right"
      overflowLineNumber={1}
      title={name}>
      <BoldTypography>{name}</BoldTypography>
    </CustomTooltip>
  );
};

export const ProductVariationsAccordion = (
  props: ProductVariationsAccordionProps,
) => {
  const [showCounts, setShowCounts] = useState(
    props.productVariationSizes.reduce((acc, item) => {
      acc[item.id] = item.flavors.some((flavor) => flavor.order);

      return acc;
    }, {} as Record<number, boolean>),
  );
  const handleAccordionChange = (sizeId: number, value: boolean) => () => {
    setShowCounts((prevShowCounts) => {
      const newShowCounts = { ...prevShowCounts };
      newShowCounts[sizeId] = value;

      return newShowCounts;
    });
  };

  const handleSizeDragOrder = (oldIndex: number, newIndex: number) => {
    const newProductVariationSizes = moveArrayItem(
      props.productVariationSizes,
      oldIndex,
      newIndex,
    );

    newProductVariationSizes.forEach((item, index) => {
      item.order = index + 1;
    });
    props.onChange(newProductVariationSizes);
  };

  const handleFlavorDragOrder =
    (sizeId: number) => (oldIndex: number, newIndex: number) => {
      const newProductVariationSizes = props.productVariationSizes.map(
        (item) => {
          if (item.id === sizeId) {
            let newFlavors = moveArrayItem(item.flavors, oldIndex, newIndex);

            newFlavors.forEach((flavor, index) => {
              if (flavor.order) flavor.order = index + 1;
            });

            if (props.defaultSize === item.name) {
              newFlavors = updateFlavorImages(newFlavors);
            }

            return { ...item, flavors: newFlavors };
          }

          return item;
        },
      );
      props.onChange(newProductVariationSizes);
    };

  const handleCheckBoxChange = (
    path: string | (string | number)[],
    value: any,
  ) => {
    const newProductVariationSizes = [...props.productVariationSizes];

    set(newProductVariationSizes, path, value);

    if (path.includes('flavors')) {
      newProductVariationSizes.forEach((size) => {
        renderArrayBasedOnOrderSort(size.flavors).forEach((flavor, index) => {
          if (flavor.order) {
            flavor.order = index + 1;
          }
        });

        if (props.defaultSize === size.name) {
          size.flavors = updateFlavorImages(size.flavors);

          if (size.flavors.every((flavor) => !flavor.order)) {
            props.handleDefaultChange(undefined);
          }
        }
      });
    }

    props.onChange(newProductVariationSizes);
  };

  const handleCheckAll =
    (field: 'flavors' | 'info', sizeIndex: number, value: boolean) => () => {
      const newProductVariationSizes = cloneDeep(props.productVariationSizes);
      const currentSize = newProductVariationSizes[sizeIndex];

      if (field === 'flavors') {
        currentSize.flavors = currentSize.flavors.map((item, index) => ({
          ...item,
          order: value ? index + 1 : 0,
        }));
      } else {
        currentSize.showCalories = value;
        currentSize.showLabel = value;
        currentSize.showPrice = value;
        currentSize.showFlavors = value;
      }

      if (
        props.defaultSize === currentSize.name &&
        currentSize.flavors.every((flavor) => !flavor.order)
      ) {
        props.handleDefaultChange(undefined);
      }

      props.onChange(newProductVariationSizes);
    };

  return (
    <SortableList
      lockAxis="y"
      draggedItemClassName="dragged"
      onSortEnd={handleSizeDragOrder}
      allowDrag={props.productVariationSizes.some((size) =>
        size.flavors.some((flavor) => flavor.order),
      )}>
      {props.productVariationSizes.map((productVariationSize, sizeIndex) => {
        const showFlavorIndeterminate = productVariationSize.flavors.some(
          (item) => item.order,
        );
        const showInfoIndeterminate =
          productVariationSize.showCalories ||
          productVariationSize.showLabel ||
          productVariationSize.showPrice;

        const hasOrder = productVariationSize.flavors.some(
          (item) => item.order,
        );

        const flavorCounts = flavourWithOrderKey(
          productVariationSize.flavors,
        ).length;

        return (
          <SortableItem key={productVariationSize.id}>
            <StyledWrapper>
              <StyledAccordion
                onChange={handleAccordionChange(
                  productVariationSize.id,
                  !showCounts[productVariationSize.id] &&
                    productVariationSize.flavors.some((item) => item.order),
                )}>
                <StyledAccordionItem expandIcon={<ExpandMore />}>
                  <Grid container>
                    {hasOrder && (
                      <FlexGrid item md={4}>
                        <SortableKnob>
                          <IconButton sx={{ cursor: 'grab' }}>
                            <ColouredDragIcon />
                          </IconButton>
                        </SortableKnob>

                        {props.options?.showDefaultSizeRadio && (
                          <Radio
                            checked={
                              props.defaultSize === productVariationSize.name
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              props.handleDefaultChange(
                                productVariationSize.name,
                              );
                            }}
                          />
                        )}
                      </FlexGrid>
                    )}
                    <Grid
                      item
                      md={8}
                      sx={{ display: 'flex', alignItems: 'center' }}>
                      <VariationName
                        name={
                          getPlainText(productVariationSize?.name) || 'None'
                        }
                      />
                    </Grid>
                  </Grid>

                  {showCounts[productVariationSize.id] && !!flavorCounts && (
                    <StyledShowFlavorCount>
                      {flavorCounts}
                    </StyledShowFlavorCount>
                  )}
                </StyledAccordionItem>
                <StyledAccordionDetails>
                  {!isEmpty(productVariationSize.flavors) ? (
                    <FormControlLabel
                      label={lang.showFlavors}
                      checked={productVariationSize.flavors.every(
                        (item) => item.order,
                      )}
                      control={
                        <Checkbox
                          indeterminate={showFlavorIndeterminate}
                          onChange={handleCheckAll(
                            'flavors',
                            sizeIndex,
                            !showFlavorIndeterminate,
                          )}
                        />
                      }
                    />
                  ) : (
                    <VariationSizeNone>
                      There are no flavours for this product
                    </VariationSizeNone>
                  )}

                  <StyledOptionWrapper>
                    <SortableList
                      lockAxis="y"
                      draggedItemClassName="dragged"
                      onSortEnd={handleFlavorDragOrder(
                        productVariationSize.id,
                      )}>
                      {productVariationSize.flavors.map(
                        (flavor, flavorIndex) => (
                          <ProductVariationFlavor
                            key={flavor.productCode}
                            sortable={!!flavor.order}
                            flavor={flavor}
                            onChange={(e, checked) => {
                              handleCheckBoxChange(
                                [sizeIndex, 'flavors', flavorIndex, 'order'],
                                checked ? flavorIndex + 1 : 0,
                              );
                            }}
                          />
                        ),
                      )}
                    </SortableList>
                  </StyledOptionWrapper>
                  <StyledInfoWrapper>
                    <FormControlLabel
                      label={lang.showProductInfo}
                      checked={
                        productVariationSize.showCalories &&
                        productVariationSize.showLabel &&
                        productVariationSize.showPrice
                      }
                      control={
                        <Checkbox
                          disabled={
                            !productVariationSize.flavors.some(
                              (flavor) => flavor.order,
                            ) || !!props.isDyProductSource
                          }
                          indeterminate={showInfoIndeterminate}
                          onChange={handleCheckAll(
                            'info',
                            sizeIndex,
                            !showInfoIndeterminate,
                          )}
                        />
                      }
                    />
                    <StyledCheckBoxWrapper>
                      <FormControlLabel
                        label={lang.sizeLabel}
                        checked={productVariationSize.showLabel}
                        control={
                          <Checkbox
                            disabled={
                              !productVariationSize.flavors.some(
                                (flavor) => flavor.order,
                              ) || !!props.isDyProductSource
                            }
                            onChange={(e, checked) => {
                              handleCheckBoxChange(
                                [sizeIndex, 'showLabel'],
                                checked,
                              );
                            }}
                          />
                        }
                      />
                      <FormControlLabel
                        label={lang.productInfoPrice}
                        checked={productVariationSize.showPrice}
                        control={
                          <Checkbox
                            disabled={
                              !productVariationSize.flavors.some(
                                (flavor) => flavor.order,
                              )
                            }
                            onChange={(e, checked) => {
                              handleCheckBoxChange(
                                [sizeIndex, 'showPrice'],
                                checked,
                              );
                            }}
                          />
                        }
                      />
                      <FormControlLabel
                        label={lang.productInfoNutrition}
                        checked={productVariationSize.showCalories}
                        control={
                          <Checkbox
                            disabled={
                              !productVariationSize.flavors.some(
                                (flavor) => flavor.order,
                              )
                            }
                            onChange={(e, checked) => {
                              handleCheckBoxChange(
                                [sizeIndex, 'showCalories'],
                                checked,
                              );
                            }}
                          />
                        }
                      />
                      <FormControlLabel
                        label={lang.productInfoFlavor}
                        checked={productVariationSize.showFlavors}
                        control={
                          <Checkbox
                            disabled={
                              !productVariationSize.flavors.some(
                                (flavor) => flavor.order,
                              ) || !!props.isDyProductSource
                            }
                            onChange={(e, checked) => {
                              handleCheckBoxChange(
                                [sizeIndex, 'showFlavors'],
                                checked,
                              );
                            }}
                          />
                        }
                      />
                    </StyledCheckBoxWrapper>
                  </StyledInfoWrapper>
                </StyledAccordionDetails>
              </StyledAccordion>
            </StyledWrapper>
          </SortableItem>
        );
      })}
    </SortableList>
  );
};
